import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Building2,
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import hotelService, { Hotel } from "../../services/hotelService";

const outlineButtonClasses =
  "border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors";

// Helper function to format field names for display
const formatFieldName = (fieldName: string): string => {
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const HotelCreate: React.FC = () => {
  const navigate = useNavigate();
  const errorRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string }>>([]);
  const [success, setSuccess] = useState<string | null>(null);
  const defaultPaymentMethods = [
    "Cash",
    "Card",
    "Credit Card",
    "Debit Card",
    "UPI",
    "Net Banking",
    "Wallet",
    "Bank Transfer",
    "Cheque",
  ];
  const [acceptedPaymentMethods, setAcceptedPaymentMethods] = useState<
    string[]
  >([]);
  const [paymentMethodInput, setPaymentMethodInput] = useState("");

  const [formData, setFormData] = useState<Partial<Hotel>>({
    hotel_name: "",
    hotel_type: "Hotel",
    chain_brand: "",
    star_rating: undefined,
    street_address: "",
    landmark: "",
    area: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    destination: "",
    reservation_contact_number: "",
    reservation_email: "",
    support_email: "",
    phone: "",
    hotel_website: "",
    checkin_time: "15:00",
    checkout_time: "11:00",
    hotel_description: "",
    terms_conditions: "",
    latitude: undefined,
    longitude: undefined,
    timezone: "",
    vervotech_id: "",
    contact_person_name: "",
    designation: "",
    contact_email: "",
    contact_phone: "",
    tax_details: "",
    bank_account_details: "",
    currency: "INR",
    markup_percentage: 0,
    service_charge_percentage: 0,
    cancellation_policy: "",
    cancellation_policy_text: "",
    cancellation_days: 0,
    cancellation_percentage: 0,
    no_show_policy: "",
    no_show_policy_text: "",
    no_show_percentage: 0,
    child_policy: "",
    child_rate: 0,
    extra_adult_rate: 0,
    extra_bed_rate: 0,
    min_stay: 1,
    max_stay: 30,
    max_advance_booking_days: 365,
    special_offer_name: "",
    special_offer_type: "Percentage Discount",
    special_offer_details: "",
    offer_valid_from: "",
    offer_valid_to: "",
    applicable_market: "",
    status: "active",
    remarks: "",
    special_conditions: "",
  });

  const handleInputChange = (field: keyof Hotel, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCoordinateChange = (
    field: "latitude" | "longitude",
    value: string
  ) => {
    const numericValue = value === "" ? undefined : parseFloat(value);
    setFormData((prev) => ({
      ...prev,
      [field]: Number.isNaN(numericValue as number) ? undefined : numericValue,
    }));
  };

  const addPaymentMethod = (method: string) => {
    const trimmed = method.trim();
    if (!trimmed) return;
    setAcceptedPaymentMethods((prev) => {
      if (prev.includes(trimmed)) return prev;
      return [...prev, trimmed];
    });
    setPaymentMethodInput("");
  };

  const removePaymentMethod = (method: string) => {
    setAcceptedPaymentMethods((prev) => prev.filter((item) => item !== method));
  };

  const handlePaymentInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      if (paymentMethodInput) {
        addPaymentMethod(paymentMethodInput);
      }
    }
  };

  const fetchTimezone = async (city: string, country: string) => {
    if (!city || !country) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/timezone/${encodeURIComponent(
          city
        )}/${encodeURIComponent(country)}`
      );
      const data = await response.json();
      if (data.timezone) {
        setFormData((prev) => ({ ...prev, timezone: data.timezone }));
      }
    } catch (error) {
      console.error("Error fetching timezone:", error);
    }
  };

  const handleCityChange = (value: string) => {
    setFormData((prev) => ({ ...prev, city: value }));
    if (value && formData.country) {
      fetchTimezone(value, formData.country);
    }
  };

  const handleCountryChange = (value: string) => {
    setFormData((prev) => ({ ...prev, country: value }));
    if (value && formData.city) {
      fetchTimezone(formData.city, value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setValidationErrors([]);
    setSuccess(null);

    try {
      // Validate required fields
      if (
        !formData.vervotech_id ||
        !formData.hotel_name ||
        !formData.city ||
        !formData.country ||
        !formData.destination ||
        !formData.contact_person_name ||
        !formData.designation ||
        !formData.contact_email ||
        !formData.contact_phone ||
        !formData.tax_details ||
        !formData.bank_account_details
      ) {
        throw new Error("Please fill in all required fields");
      }

      // Clean up form data - remove empty strings and convert to null for date fields
      const cleanedFormData = Object.fromEntries(
        Object.entries(formData)
          .map(([key, value]) => {
            if (value === "" || value === undefined) {
              if (["offer_valid_from", "offer_valid_to"].includes(key)) {
                return [key, null];
              }
              if (["latitude", "longitude", "star_rating"].includes(key)) {
                return [key, null];
              }
              if (key === "reservation_contact_number") {
                return [key, null];
              }
              return [key, undefined];
            }
            return [key, value];
          })
          .filter(([, value]) => value !== undefined)
      ) as Record<string, unknown>;

      if (acceptedPaymentMethods.length > 0) {
        cleanedFormData.accepted_payment_methods = acceptedPaymentMethods;
      }

      const response = await hotelService.createHotel(cleanedFormData);
      setSuccess("Hotel created successfully!");

      // Redirect to hotels list after a short delay
      setTimeout(() => {
        navigate("/hotels");
      }, 1500);
    } catch (err: any) {
      // Check if there are detailed validation errors
      if (
        err.response?.data?.errors &&
        Array.isArray(err.response.data.errors)
      ) {
        const errors = err.response.data.errors.map((error: any) => {
          const fieldName = error.path || error.param || "Field";
          return {
            field: formatFieldName(fieldName),
            message: error.msg || error.message || "Validation error",
          };
        });
        
        setValidationErrors(errors);
        setError(
          err.response?.data?.message || "Validation failed. Please fix the errors below."
        );
      } else {
        setValidationErrors([]);
        setError(
          err.response?.data?.message || err.message || "Failed to create hotel"
        );
      }

      // Scroll to top to show error message after state updates
      setTimeout(() => {
        errorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const requiredFields = [
    "vervotech_id",
    "hotel_name",
    "city",
    "country",
    "destination",
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/hotels")}
          className={outlineButtonClasses}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Hotels
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">Add New Hotel</h1>
          <p className="text-muted-foreground">Create a new hotel property</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="flex items-center space-x-2 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          <span>{success}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          ref={errorRef}
          className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold mb-2">{error}</div>
              {validationErrors.length > 0 && (
                <ul className="list-disc list-inside space-y-1 mt-2 ml-2">
                  {validationErrors.map((err, index) => (
                    <li key={index}>
                      <span className="font-medium">{err.field}:</span> {err.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Building2 className="w-5 h-5" />
              <span>Basic Information</span>
            </CardTitle>
            <CardDescription className="text-gray-400">
              Essential hotel details and identification
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Vervotech ID <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.vervotech_id || ""}
                  onChange={(e) =>
                    handleInputChange("vervotech_id", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter Vervotech ID"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Hotel Name <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.hotel_name || ""}
                  onChange={(e) =>
                    handleInputChange("hotel_name", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter hotel name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Hotel Type
                </label>
                <select
                  value={formData.hotel_type || "Hotel"}
                  onChange={(e) =>
                    handleInputChange("hotel_type", e.target.value)
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Hotel">Hotel</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Resort">Resort</option>
                  <option value="Hostel">Hostel</option>
                  <option value="Guesthouse">Guesthouse</option>
                  <option value="Villa">Villa</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Chain/Brand
                </label>
                <Input
                  value={formData.chain_brand || ""}
                  onChange={(e) =>
                    handleInputChange("chain_brand", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter chain or brand name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Star Rating
                </label>
                <select
                  value={formData.star_rating || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "star_rating",
                      parseInt(e.target.value) || 0
                    )
                  }
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select rating</option>
                  <option value="1">1 Star</option>
                  <option value="2">2 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="5">5 Stars</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={formData.status || "active"}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Owner / Management Information */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Owner / Management</CardTitle>
            <CardDescription className="text-gray-400">
              Primary contact details for management and settlements
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Person Name <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.contact_person_name || ""}
                  onChange={(e) =>
                    handleInputChange("contact_person_name", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter contact person name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Designation <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.designation || ""}
                  onChange={(e) =>
                    handleInputChange("designation", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Owner / Manager / Other"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Email <span className="text-red-400">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.contact_email || ""}
                  onChange={(e) =>
                    handleInputChange("contact_email", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="management@hotel.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Mobile / Landline{" "}
                  <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.contact_phone || ""}
                  onChange={(e) =>
                    handleInputChange("contact_phone", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="+91XXXXXXXXXX"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tax Details (PAN / GST / VAT){" "}
                <span className="text-red-400">*</span>
              </label>
              <textarea
                value={formData.tax_details || ""}
                onChange={(e) =>
                  handleInputChange("tax_details", e.target.value)
                }
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter tax registration numbers and related information"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bank Account Details <span className="text-red-400">*</span>
              </label>
              <textarea
                value={formData.bank_account_details || ""}
                onChange={(e) =>
                  handleInputChange("bank_account_details", e.target.value)
                }
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Include account name, number, bank, branch, IFSC / SWIFT etc."
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Location Information */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Location Information</CardTitle>
            <CardDescription className="text-gray-400">
              Address and geographical details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Street Address
                </label>
                <Input
                  value={formData.street_address || ""}
                  onChange={(e) =>
                    handleInputChange("street_address", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter street address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Area
                </label>
                <Input
                  value={formData.area || ""}
                  onChange={(e) => handleInputChange("area", e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter area"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Landmark
                </label>
                <Input
                  value={formData.landmark || ""}
                  onChange={(e) =>
                    handleInputChange("landmark", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter nearby landmark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  City <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.city || ""}
                  onChange={(e) => handleCityChange(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter city"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  State
                </label>
                <Input
                  value={formData.state || ""}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter state"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Zip Code
                </label>
                <Input
                  value={formData.zip_code || ""}
                  onChange={(e) =>
                    handleInputChange("zip_code", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter zip code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Country <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.country || ""}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter country"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Destination <span className="text-red-400">*</span>
                </label>
                <Input
                  value={formData.destination || ""}
                  onChange={(e) =>
                    handleInputChange("destination", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter destination"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Latitude
                </label>
                <Input
                  type="number"
                  step="0.000001"
                  value={
                    formData.latitude !== undefined &&
                    formData.latitude !== null
                      ? formData.latitude.toString()
                      : ""
                  }
                  onChange={(e) =>
                    handleCoordinateChange("latitude", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="12.9716"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Longitude
                </label>
                <Input
                  type="number"
                  step="0.000001"
                  value={
                    formData.longitude !== undefined &&
                    formData.longitude !== null
                      ? formData.longitude.toString()
                      : ""
                  }
                  onChange={(e) =>
                    handleCoordinateChange("longitude", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="77.5946"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Contact Information</CardTitle>
            <CardDescription className="text-gray-400">
              Phone, email, and website details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone
                </label>
                <Input
                  value={formData.phone || ""}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Reservation Contact Number
                </label>
                <Input
                  value={formData.reservation_contact_number || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "reservation_contact_number",
                      e.target.value
                    )
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Front desk contact number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Hotel Website
                </label>
                <Input
                  value={formData.hotel_website || ""}
                  onChange={(e) =>
                    handleInputChange("hotel_website", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter website URL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Reservation Email
                </label>
                <Input
                  type="email"
                  value={formData.reservation_email || ""}
                  onChange={(e) =>
                    handleInputChange("reservation_email", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter reservation email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Support Email
                </label>
                <Input
                  type="email"
                  value={formData.support_email || ""}
                  onChange={(e) =>
                    handleInputChange("support_email", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter support email"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Additional Information</CardTitle>
            <CardDescription className="text-gray-400">
              Check-in/out times, description, and other details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Check-in Time
                </label>
                <Input
                  type="time"
                  value={formData.checkin_time || "15:00"}
                  onChange={(e) =>
                    handleInputChange("checkin_time", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Check-out Time
                </label>
                <Input
                  type="time"
                  value={formData.checkout_time || "11:00"}
                  onChange={(e) =>
                    handleInputChange("checkout_time", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Currency
                </label>
                <Input
                  value={formData.currency || "INR"}
                  onChange={(e) =>
                    handleInputChange("currency", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter currency code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Timezone
                </label>
                <Input
                  value={formData.timezone || ""}
                  onChange={(e) =>
                    handleInputChange("timezone", e.target.value)
                  }
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Enter timezone"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Accepted Payment Methods
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {acceptedPaymentMethods.length === 0 && (
                  <span className="text-sm text-gray-500">
                    No payment methods added yet.
                  </span>
                )}
                {acceptedPaymentMethods.map((method) => (
                  <span
                    key={method}
                    className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-blue-900/30 border border-blue-700 text-blue-300"
                  >
                    {method}
                    <button
                      type="button"
                      className="ml-2 text-blue-300 hover:text-blue-100"
                      onClick={() => removePaymentMethod(method)}
                      aria-label={`Remove ${method}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              <Input
                value={paymentMethodInput}
                onChange={(e) => setPaymentMethodInput(e.target.value)}
                onKeyDown={handlePaymentInputKeyDown}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="Type a payment method and press Enter"
              />
              <p className="text-xs text-gray-400 mt-2">
                Press Enter or comma to add. Choose from suggestions or type
                your own methods.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {defaultPaymentMethods.map((option) => {
                  const isSelected = acceptedPaymentMethods.includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => addPaymentMethod(option)}
                      disabled={isSelected}
                      className={`px-3 py-1 text-sm rounded-md border ${
                        isSelected
                          ? "border-blue-700 text-blue-300 bg-blue-900/20 cursor-not-allowed"
                          : "border-gray-700 text-gray-300 hover:border-blue-600 hover:text-blue-300"
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Hotel Description
              </label>
              <textarea
                value={formData.hotel_description || ""}
                onChange={(e) =>
                  handleInputChange("hotel_description", e.target.value)
                }
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Enter hotel description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Remarks
              </label>
              <textarea
                value={formData.remarks || ""}
                onChange={(e) => handleInputChange("remarks", e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter any remarks"
              />
            </div>
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/hotels")}
            className="border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Hotel
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default HotelCreate;
